import { Routes } from '@angular/router';
import { authGuard, guestGuard, rootRedirectGuard } from './core/auth/auth.guard';
import { LISA_ROLES } from './core/auth/lisa-roles';
import { ROLE_ROUTE_DATA_KEY, roleGuard } from './core/auth/role.guard';
import type { RoleRef } from './core/auth/role-ref.types';

const SYSTEM_ROLES: RoleRef[] = [LISA_ROLES.SUPER_ADMIN, LISA_ROLES.COUNTRY_ADMIN];
const PARTNER_ORG_ROLES: RoleRef[] = [LISA_ROLES.PARTNER_ADMIN, LISA_ROLES.PARTNER_VET];
const ALL_ADMIN_ROLES: RoleRef[] = [...SYSTEM_ROLES, ...PARTNER_ORG_ROLES];
const FARMER_WRITE_ROLES: RoleRef[] = [...SYSTEM_ROLES, LISA_ROLES.PARTNER_ADMIN, LISA_ROLES.PARTNER_VET];

const withAccess = (roles: RoleRef[], resource?: string) => ({
  canActivate: [authGuard, roleGuard],
  data: { [ROLE_ROUTE_DATA_KEY]: roles, resource },
});

export const appRoutes: Routes = [
  { path: '', canActivate: [rootRedirectGuard], children: [] },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./shared/layouts/admin-layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        ...withAccess(ALL_ADMIN_ROLES, 'DASHBOARD_RESOURCE'),
        loadComponent: () => import('./pages/admin/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'organizations',
        ...withAccess(SYSTEM_ROLES, 'ORGANIZATIONS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/organizations/organizations.component').then(m => m.OrganizationsComponent),
      },
      {
        path: 'organizations/add',
        ...withAccess(SYSTEM_ROLES, 'ORGANIZATIONS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/add-organization/add-organization.component').then(m => m.AddOrganizationComponent),
      },
      {
        path: 'users',
        ...withAccess([...SYSTEM_ROLES, LISA_ROLES.PARTNER_ADMIN], 'USERS_RESOURCE'),
        loadComponent: () => import('./pages/admin/users/users.component').then(m => m.UsersComponent),
      },
      {
        path: 'users/add',
        ...withAccess([...SYSTEM_ROLES, LISA_ROLES.PARTNER_ADMIN], 'USERS_RESOURCE'),
        loadComponent: () => import('./pages/admin/add-user/add-user-page.component').then(m => m.AddUserPageComponent),
      },
      {
        path: 'roles',
        ...withAccess(SYSTEM_ROLES, 'ROLES_PERMISSIONS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/roles-permissions/roles-permissions.component').then(m => m.RolesPermissionsComponent),
      },
      {
        path: 'access-requests',
        ...withAccess(PARTNER_ORG_ROLES, 'ACCESS_REQUESTS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/access-requests/access-requests.component').then(m => m.AccessRequestsComponent),
      },
      {
        path: 'farmers',
        ...withAccess(ALL_ADMIN_ROLES, 'FARMERS_RESOURCE'),
        loadComponent: () => import('./pages/admin/farmers/farmers.component').then(m => m.FarmersComponent),
      },
      {
        path: 'pending-validations',
        ...withAccess([...SYSTEM_ROLES, LISA_ROLES.PARTNER_ADMIN], 'PENDING_VALIDATIONS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/pending-validation/pending-validation').then(m => m.PendingValidationComponent),
      },
      {
        path: 'farmers/add',
        canActivate: [authGuard, roleGuard],
        data: { farmerFormMode: 'add', [ROLE_ROUTE_DATA_KEY]: FARMER_WRITE_ROLES, resource: 'FARMERS_RESOURCE' },
        loadComponent: () => import('./pages/admin/add-farmer/add-farmer.component').then(m => m.AddFarmerComponent),
      },
      {
        path: 'farmers/:id/edit',
        canActivate: [authGuard, roleGuard],
        data: { farmerFormMode: 'edit', [ROLE_ROUTE_DATA_KEY]: FARMER_WRITE_ROLES, resource: 'FARMERS_RESOURCE' },
        loadComponent: () => import('./pages/admin/add-farmer/add-farmer.component').then(m => m.AddFarmerComponent),
      },
      {
        path: 'farmers/:id',
        ...withAccess(ALL_ADMIN_ROLES, 'FARMERS_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/farmer-detail/farmer-detail.component').then(m => m.FarmerDetailComponent),
      },
      {
        path: 'audit',
        ...withAccess([...SYSTEM_ROLES, LISA_ROLES.PARTNER_ADMIN], 'AUDIT_LOG_RESOURCE'),
        loadComponent: () => import('./pages/admin/audit-log/audit-log.component').then(m => m.AuditLogComponent),
      },
      {
        path: 'access-log',
        ...withAccess([LISA_ROLES.SUPER_ADMIN], 'ACCESS_LOG_RESOURCE'),
        loadComponent: () => import('./pages/admin/access-log/access-log.component').then(m => m.AccessLogComponent),
      },
      {
        path: 'settings',
        ...withAccess(SYSTEM_ROLES, 'REFERENCE_LIST_RESOURCE'),
        loadComponent: () => import('./pages/admin/settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'kpi-data-sources',
        ...withAccess(SYSTEM_ROLES, 'KPI_DATA_SOURCE_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/kpi-data-sources/kpi-data-sources.component').then(m => m.KpiDataSourcesComponent),
      },
      {
        path: 'kpi-definitions',
        ...withAccess(SYSTEM_ROLES, 'KPI_DEFINITION_RESOURCE'),
        loadComponent: () =>
          import('./pages/admin/kpi-definitions/kpi-definitions.component').then(m => m.KpiDefinitionsComponent),
      },
      {
        path: '**',
        loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent),
      },
    ],
  },
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
];
